import { getRandomValues } from "crypto";

/**

 * Encodes a utf8 string as a byte array.
 * @param {String} str 
 * @returns {Uint8Array}
 */
export function str2buf(str) {
  return new TextEncoder("utf-8").encode(str).buffer;
}
/**
 * Decodes a byte array as a utf8 string.
 * @param {Uint8Array} buffer
 * @returns {String}
 */
export function buf2str(buffer) {
  // console.log(new TextDecoder("utf-8").decode(buffer))
  return new TextDecoder("utf-8").decode(buffer);
}

/**
 * Decodes a string of hex to a byte array.
 * @param {String} hexStr
 * @returns {Uint8Array}
 */
export function hex2buf(hexStr) {
  return new Uint8Array(hexStr.match(/.{2}/g).map((h) => parseInt(h, 16)));
}

/**
 * Encodes a byte array as a string of hex.
 * @param {Uint8Array} buffer
 * @returns {String}
 */
export function buf2hex(buffer) {
  return Array.prototype.slice
    .call(new Uint8Array(buffer))
    .map((x) => [x >> 4, x & 15])
    .map((ab) => ab.map((x) => x.toString(16)).join(""))
    .join("");
}

/**
 * Given a passphrase, this generates a crypto key
 * using `PBKDF2` with SHA256 and 1000 iterations.
 * If no salt is given, a new one is generated.
 * The return value is an array of `[key, salt]`.
 * @param {String} passphrase
 * @param {UInt8Array} salt [salt=random bytes]
 * @returns {Promise<[CryptoKey,UInt8Array]>}
 */
function deriveKey(passphrase, salt) {
  salt = salt || crypto.getRandomValues(new Uint8Array(8));
  return crypto.subtle
    .importKey("raw", str2buf(passphrase), "PBKDF2", false, ["deriveKey"])
    .then((key) =>
      crypto.subtle.deriveKey(
        {
          name: "PBKDF2",
          salt: str2buf(salt),
          iterations: 1000,
          hash: "SHA-256",
        },
        key,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
      )
    )
    .then((key) => {
      return [key, salt];
    });
}

/**
 * Given a passphrase and some plaintext, this derives a key
 * (generating a new salt), and then encrypts the plaintext with the derived
 * key using AES-GCM. The ciphertext, salt, and iv are hex encoded and joined
 * by a "-". So the result is `"salt-iv-ciphertext"`.
 * @param {String} passphrase
 * @param {String} plaintext
 * @returns {Promise<String>}
 */
export function encrypt(passphrase, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = plaintext;
  return deriveKey(passphrase).then(([key, salt]) =>
    crypto.subtle
      .encrypt({ name: "AES-GCM", iv }, key, data)
      .then((ciphertext) => {
        return `${buf2hex(salt)}-${buf2hex(iv)}-${buf2hex(ciphertext)}`;
      })
  );
}
export function encryptText(passphrase, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = str2buf(plaintext);
  return deriveKey(passphrase).then(([key, salt]) =>
    crypto.subtle
      .encrypt({ name: "AES-GCM", iv }, key, data)
      .then((ciphertext) => {
        return `${buf2hex(salt)}-${buf2hex(iv)}-${buf2hex(ciphertext)}`;
      })
      .catch((e) => {
        console.log(e);
        throw new Error();
      })
  );
}

/**
 * Given a key and ciphertext (in the form of a string) as given by `encrypt`,
 * this decrypts the ciphertext and returns the original plaintext
 * @param {String} passphrase
 * @param {String} saltIvCipherHex
 * @returns {Promise<String>}
 */
export function decrypt(passphrase, saltIvCipherHex) {
  const [salt, iv, data] = saltIvCipherHex.split("-").map(hex2buf);
  return deriveKey(passphrase, salt)
    .then(([key]) => crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data))
    .then((v) => {
      return v;
    });
  // .then(v => buf2str(new Uint8Array(v)));
}
export function decryptText(passphrase, saltIvCipherHex, key_) {
  /*
    A field that is not there decrypts to nothing -- it does not throw.

    Callers wrap several of these in one try and rebuild a whole asset or
    beneficiary from the result, so a synchronous throw here does not lose one
    field, it loses the record. That happens for real in two places: a document
    written before a field existed, and the partner view, where the server
    deliberately omits every field the customer did not share.

    `""` rather than "" is the same sentinel the catch below returns, so the
    JSON.parse every caller does still yields an empty string.
  */
  if (typeof saltIvCipherHex !== "string" || saltIvCipherHex === "")
    return Promise.resolve(`""`);
  const [salt, iv, data] = saltIvCipherHex.split("-").map(hex2buf);
  return deriveKey(passphrase, salt)
    .then(([key]) => crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data))
    .then((v) => {
      return v;
    })
    .then((v) => buf2str(new Uint8Array(v)))
    .catch((e) => {
      console.log(e);
      console.log("Error");
      console.log(saltIvCipherHex);
      return `""`;
    });
}

/**
 * Given a plaintext, salt, iteration number and key length,
 * this returns th SHA-256 PBDKF2 hash and the salt
 * @param {String} passphrase
 * @param {String} salt
 * @param {Number} iterations
 * @param {Number} length
 * @returns {Promise<String>}
 */

export async function pbdkf2(passphrase, salt, iterations, length) {
  salt = salt || getRandomValues(new Uint8Array(8));
  const key = await crypto.subtle.importKey(
    "raw",
    str2buf(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const devKey = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: str2buf(salt),
      iterations: iterations,
      hash: "SHA-256",
    },
    key,
    { name: "AES-GCM", length: length },
    true,
    ["encrypt", "decrypt"]
  );
  const keyBytes = await crypto.subtle.exportKey("raw", devKey);
  return [buf2hex(keyBytes), salt]; // return [hash, salt]
}

export async function encryptFileAES(passphrase, file) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await file.arrayBuffer();
  return deriveKey(passphrase).then(([key, salt]) =>
    crypto.subtle
      .encrypt({ name: "AES-GCM", iv }, key, data)
      .then((ciphertext) => {
        const saltBlob = new Blob([salt]);
        // console.log(saltBlob.size);
        const ivBlob = new Blob([iv]);
        // console.log(ivBlob.size);
        const ciphertextBlob = new Blob([ciphertext]);
        const file_ = new File(
          [saltBlob, ivBlob, ciphertextBlob],
          "encrypted_" + file.name
        );
        // console.log(file_);
        return file_;
      })
      .catch((e) => {
        console.log(e);
      })
  );
}

async function extractAesGcmDataFromFile(encryptedFile) {
  const reader = new FileReader();
  reader.readAsArrayBuffer(encryptedFile);

  return new Promise((resolve, reject) => {
    reader.onload = async function () {
      const arrayBuffer = reader.result;
      // const dataView = new DataView(arrayBuffer); ##Commented to avoid warning

      // Get salt
      const saltSize = 8; // Size of salt in bytes
      const salt = new Uint8Array(arrayBuffer.slice(0, saltSize));

      // Get IV
      const ivSize = 12; // Size of IV in bytes
      const iv = new Uint8Array(arrayBuffer.slice(saltSize, saltSize + ivSize));

      // Get ciphertext
      const ciphertext = new Uint8Array(arrayBuffer.slice(saltSize + ivSize));

      resolve({ salt, iv, ciphertext });
    };

    reader.onerror = reject;
  });
}
export async function decryptFileAES(passphrase, file) {
  const { salt, iv, ciphertext } = await extractAesGcmDataFromFile(file);

  // console.log(salt);
  // console.log(iv);
  // console.log(ciphertext);
  return deriveKey(passphrase, salt)
    .then(([key]) =>
      crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext)
    )
    .then((v) => {
      // console.log(v);
      const decryptedBlob = new Blob([v]);
      // console.log(decryptedBlob);
      const decryptedFile = new File([decryptedBlob], file.name);
      // console.log(decryptedFile);
      return decryptedFile;
    })
    .catch((error) => {
      console.log(error);
      console.log("An error occurred:", error.message);
      console.error(error.stack);
    });
}

/* ---------------------------------------------------------------------------
   Partner keypairs

   The first asymmetric crypto in the app, and it exists for one job: letting a
   customer hand their own key to their partner without the server ever seeing
   it.

   Everything else here is symmetric and derived from a password, which cannot
   do this. To wrap a secret for somebody else you need something of theirs that
   is safe to publish, and only a public key is. A shared symmetric secret would
   have to travel to the customer's browser, and the server would see it on the
   way.

   RSA-OAEP rather than ECDH: the payload is one short key, there is no session
   to negotiate, and this keeps the whole operation to a single wrap and unwrap
   with no ephemeral keys to manage.
   --------------------------------------------------------------------------- */

const RSA_PARAMS = {
  name: "RSA-OAEP",
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: "SHA-256",
};

const bufToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  // Chunked: spreading a large array into fromCharCode blows the call stack.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + 0x8000)
    );
  }
  return btoa(binary);
};

/*
  Returns the view rather than its .buffer. Both are valid to WebCrypto, but an
  ArrayBuffer created in one realm fails the instanceof check in another -- which
  is exactly what happens under jsdom. A typed array does not have that problem.
*/
const base64ToBuf = (text) => {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/*
  A fresh keypair, exported as base64. The public half is stored in the clear;
  the private half must be wrapped before it goes anywhere — see
  wrapPrivateKey.
*/
export async function generateKeyPair() {
  const pair = await crypto.subtle.generateKey(RSA_PARAMS, true, [
    "encrypt",
    "decrypt",
  ]);
  const [publicKey, privateKey] = await Promise.all([
    crypto.subtle.exportKey("spki", pair.publicKey),
    crypto.subtle.exportKey("pkcs8", pair.privateKey),
  ]);
  return {
    public_key: bufToBase64(publicKey),
    private_key: bufToBase64(privateKey),
  };
}

/*
  The private key, encrypted under a passphrase with the same AES-GCM helper
  everything else uses. Wrap it once per way in — the password-derived secret
  and the recovery code — so losing one is survivable.
*/
export function wrapPrivateKey(passphrase, private_key_base64) {
  return encryptText(passphrase, private_key_base64);
}

export function unwrapPrivateKey(passphrase, wrapped) {
  return decryptText(passphrase, wrapped);
}

/*
  Encrypt a short secret — in practice a customer's key — for the holder of this
  public key. RSA-OAEP with a 2048-bit modulus and SHA-256 leaves room for 190
  bytes, which is far more than a 64-character key needs.
*/
export async function encryptForPublicKey(public_key_base64, plaintext) {
  const key = await crypto.subtle.importKey(
    "spki",
    base64ToBuf(public_key_base64),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    key,
    new Uint8Array(str2buf(plaintext))
  );
  return bufToBase64(encrypted);
}

export async function decryptWithPrivateKey(private_key_base64, ciphertext) {
  const key = await crypto.subtle.importKey(
    "pkcs8",
    base64ToBuf(private_key_base64),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["decrypt"]
  );
  const decrypted = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    key,
    base64ToBuf(ciphertext)
  );
  return buf2str(decrypted);
}
